import { supabase } from '../lib/supabase'
import { handleDatabaseError, withErrorHandling, createFallbackResponse, isDatabaseSetupError, logDatabaseSetupInstructions } from '../utils/errorHandler'

// Quiz management service
export class QuizService {
  constructor() {
    this.questionTypes = [
      'multiple-choice',
      'true-false',
      'multiple-select',
      'fill-in-blank',
      'matching',
      'ordering'
    ]
    
    this.difficultyLevels = ['beginner', 'intermediate', 'advanced']
    
    this.categories = [
      'phishing',
      'passwords',
      'data-protection',
      'social-engineering',
      'malware',
      'network-security',
      'incident-response',
      'compliance',
      'general'
    ]
  }

  // Create a new quiz
  async createQuiz(quizData) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Validate required fields
    if (!quizData.title || !quizData.category || !quizData.difficulty) {
      throw new Error('Title, category, and difficulty are required')
    }

    // Validate category and difficulty
    if (!this.categories.includes(quizData.category)) {
      throw new Error(`Invalid category. Must be one of: ${this.categories.join(', ')}`)
    }

    if (!this.difficultyLevels.includes(quizData.difficulty)) {
      throw new Error(`Invalid difficulty. Must be one of: ${this.difficultyLevels.join(', ')}`)
    }

    const quiz = {
      title: quizData.title,
      description: quizData.description || '',
      category: quizData.category,
      difficulty: quizData.difficulty,
      time_limit: quizData.timeLimit || null,
      passing_score: quizData.passingScore || 70,
      max_attempts: quizData.maxAttempts || null,
      show_correct_answers: quizData.showCorrectAnswers !== false,
      randomize_questions: quizData.randomizeQuestions || false,
      randomize_options: quizData.randomizeOptions || false,
      course_id: quizData.courseId || null,
      tags: quizData.tags || [],
      created_by: user.id
    }

    return await withErrorHandling(async () => {
      const { data, error } = await supabase
        .from('quizzes')
        .insert([quiz])
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    }, 'quiz creation')
  }

  // Add question to quiz
  async addQuestion(quizId, questionData) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Validate question type
    if (!this.questionTypes.includes(questionData.type)) {
      throw new Error(`Invalid question type. Must be one of: ${this.questionTypes.join(', ')}`)
    }

    // Validate required fields
    if (!questionData.question || !questionData.options || questionData.options.length < 2) {
      throw new Error('Question text and at least 2 options are required')
    }

    // Validate correct answers
    if (!questionData.correctAnswers || questionData.correctAnswers.length === 0) {
      throw new Error('At least one correct answer is required')
    }

    // Get current question count for order
    const { count } = await supabase
      .from('quiz_questions')
      .select('*', { count: 'exact', head: true })
      .eq('quiz_id', quizId)

    const question = {
      quiz_id: quizId,
      question_type: questionData.type,
      question_text: questionData.question,
      options: questionData.options,
      correct_answers: questionData.correctAnswers,
      explanation: questionData.explanation || '',
      points: questionData.points || 1,
      order_index: count || 0,
      media_url: questionData.mediaUrl || null,
      time_limit: questionData.timeLimit || null
    }

    const { data, error } = await supabase
      .from('quiz_questions')
      .insert([question])
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  }

  // Update question
  async updateQuestion(questionId, updates) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Validate question type if provided
    if (updates.question_type && !this.questionTypes.includes(updates.question_type)) {
      throw new Error(`Invalid question type. Must be one of: ${this.questionTypes.join(', ')}`)
    }

    const { data, error } = await supabase
      .from('quiz_questions')
      .update(updates)
      .eq('id', questionId)
      .select(`
        *,
        quizzes!quiz_id (
          created_by
        )
      `)
      .single()

    if (error) {
      throw error
    }

    // Check if user owns the quiz
    if (data.quizzes.created_by !== user.id) {
      throw new Error('Access denied')
    }

    return data
  }

  // Delete question
  async deleteQuestion(questionId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get question with quiz info
    const { data: question, error: fetchError } = await supabase
      .from('quiz_questions')
      .select(`
        *,
        quizzes!quiz_id (
          created_by
        )
      `)
      .eq('id', questionId)
      .single()

    if (fetchError || !question) {
      throw new Error('Question not found')
    }

    // Check if user owns the quiz
    if (question.quizzes.created_by !== user.id) {
      throw new Error('Access denied')
    }

    const { error } = await supabase
      .from('quiz_questions')
      .delete()
      .eq('id', questionId)

    if (error) {
      throw error
    }

    return { success: true }
  }

  // Update quiz
  async updateQuiz(quizId, updates) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Validate category and difficulty if provided
    if (updates.category && !this.categories.includes(updates.category)) {
      throw new Error(`Invalid category. Must be one of: ${this.categories.join(', ')}`)
    }

    if (updates.difficulty && !this.difficultyLevels.includes(updates.difficulty)) {
      throw new Error(`Invalid difficulty. Must be one of: ${this.difficultyLevels.join(', ')}`)
    }

    const { data, error } = await supabase
      .from('quizzes')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', quizId)
      .eq('created_by', user.user.id)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  }

  // Delete quiz
  async deleteQuiz(quizId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Check if quiz has attempts
    const { data: attempts, error: attemptError } = await supabase
      .from('quiz_attempts')
      .select('id')
      .eq('quiz_id', quizId)
      .limit(1)

    if (attemptError) {
      throw attemptError
    }

    if (attempts && attempts.length > 0) {
      throw new Error('Cannot delete quiz with existing attempts. Unpublish the quiz instead.')
    }

    // Delete questions first
    const { error: questionsError } = await supabase
      .from('quiz_questions')
      .delete()
      .eq('quiz_id', quizId)

    if (questionsError) {
      throw questionsError
    }

    // Delete the quiz
    const { error } = await supabase
      .from('quizzes')
      .delete()
      .eq('id', quizId)
      .eq('created_by', user.user.id)

    if (error) {
      throw error
    }

    return { success: true }
  }

  // Get quiz by ID with questions
  async getQuiz(quizId, includeQuestions = true) {
    let query = supabase
      .from('quizzes')
      .select('*')
      .eq('id', quizId)
      .single()

    const { data: quiz, error } = await query

    if (error) {
      throw error
    }

    if (includeQuestions) {
      const { data: questions, error: questionsError } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('quiz_id', quizId)
        .order('order_index', { ascending: true })

      if (questionsError) {
        throw questionsError
      }

      quiz.questions = questions || []
      quiz.total_points = questions?.reduce((sum, q) => sum + (q.points || 1), 0) || 0
    }

    return quiz
  }

  // Get all quizzes with filters
  async getQuizzes(filters = {}) {
    try {
      let query = supabase
        .from('quizzes')
        .select('*')

      // Apply filters
      if (filters.category) {
        query = query.eq('category', filters.category)
      }

      if (filters.difficulty) {
        query = query.eq('difficulty', filters.difficulty)
      }

      if (filters.createdBy) {
        query = query.eq('created_by', filters.createdBy)
      }

      if (filters.courseId) {
        query = query.eq('course_id', filters.courseId)
      }

      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
      }

      if (filters.tags && filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags)
      }

      // Apply sorting
      const sortBy = filters.sortBy || 'created_at'
      const sortOrder = filters.sortOrder || 'desc'
      query = query.order(sortBy, { ascending: sortOrder === 'asc' })

      // Apply pagination
      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
      }

      const { data, error } = await query

      if (error) {
        throw error
      }

      // Get question counts for each quiz
      const quizIds = data.map(quiz => quiz.id)
      if (quizIds.length > 0) {
        const { data: questionCounts, error: countError } = await supabase
          .from('quiz_questions')
          .select('quiz_id')
          .in('quiz_id', quizIds)

        if (!countError) {
          const counts = questionCounts.reduce((acc, q) => {
            acc[q.quiz_id] = (acc[q.quiz_id] || 0) + 1
            return acc
          }, {})

          data.forEach(quiz => {
            quiz.question_count = counts[quiz.id] || 0
          })
        }
      }

      return data
    } catch (error) {
      if (isDatabaseSetupError(error)) {
        logDatabaseSetupInstructions()
        return []
      }
      throw handleDatabaseError(error, 'fetching quizzes')
    }
  }

  // Start quiz attempt
  async startQuizAttempt(quizId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get quiz info
    const { data: quiz, error: quizError } = await supabase
      .from('quizzes')
      .select('*')
      .eq('id', quizId)
      .single()

    if (quizError || !quiz) {
      throw new Error('Quiz not found')
    }

    // Check attempt limit
    if (quiz.max_attempts) {
      const { count: attemptCount, error: countError } = await supabase
        .from('quiz_attempts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('quiz_id', quizId)

      if (countError) {
        throw countError
      }

      if (attemptCount >= quiz.max_attempts) {
        throw new Error(`Maximum attempts (${quiz.max_attempts}) reached for this quiz`)
      }
    }

    // Get questions
    let { data: questions, error: questionsError } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('quiz_id', quizId)
      .order('order_index', { ascending: true })

    if (questionsError) {
      throw questionsError
    }

    // Randomize questions if enabled
    if (quiz.randomize_questions) {
      questions = this.shuffleArray([...questions])
    }

    // Randomize options if enabled
    if (quiz.randomize_options) {
      questions = questions.map(q => ({
        ...q,
        options: this.shuffleArray([...q.options])
      }))
    }

    // Create attempt record
    const attempt = {
      user_id: user.user.id,
      quiz_id: quizId,
      started_at: new Date().toISOString(),
      questions_data: questions,
      user_answers: {},
      time_remaining: quiz.time_limit
    }

    const { data, error } = await supabase
      .from('quiz_attempts')
      .insert([attempt])
      .select()
      .single()

    if (error) {
      throw error
    }

    return {
      ...data,
      quiz,
      questions: questions.map(q => ({
        id: q.id,
        question_type: q.question_type,
        question_text: q.question_text,
        options: q.options,
        points: q.points,
        media_url: q.media_url,
        time_limit: q.time_limit
      }))
    }
  }

  // Submit quiz answer
  async submitQuizAnswer(attemptId, questionId, answer) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get attempt
    const { data: attempt, error: attemptError } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('id', attemptId)
      .eq('user_id', user.id)
      .single()

    if (attemptError || !attempt) {
      throw new Error('Quiz attempt not found')
    }

    if (attempt.completed_at) {
      throw new Error('Quiz attempt already completed')
    }

    // Update user answers
    const userAnswers = { ...attempt.user_answers }
    userAnswers[questionId] = {
      answer,
      answered_at: new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('quiz_attempts')
      .update({ user_answers: userAnswers })
      .eq('id', attemptId)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  }

  // Complete quiz attempt
  async completeQuizAttempt(attemptId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get attempt with quiz info
    const { data: attempt, error: attemptError } = await supabase
      .from('quiz_attempts')
      .select(`
        *,
        quizzes (
          *
        )
      `)
      .eq('id', attemptId)
      .eq('user_id', user.user.id)
      .single()

    if (attemptError || !attempt) {
      throw new Error('Quiz attempt not found')
    }

    if (attempt.completed_at) {
      throw new Error('Quiz attempt already completed')
    }

    // Calculate score
    const result = this.calculateQuizScore(attempt.questions_data, attempt.user_answers)
    
    const updates = {
      completed_at: new Date().toISOString(),
      score: result.score,
      total_points: result.totalPoints,
      correct_answers: result.correctCount,
      total_questions: result.totalQuestions,
      passed: result.score >= attempt.quizzes.passing_score,
      detailed_results: result.detailedResults
    }

    const { data, error } = await supabase
      .from('quiz_attempts')
      .update(updates)
      .eq('id', attemptId)
      .select(`
        *,
        quizzes (
          *
        )
      `)
      .single()

    if (error) {
      throw error
    }

    return data
  }

  // Calculate quiz score
  calculateQuizScore(questions, userAnswers) {
    let correctCount = 0
    let totalPoints = 0
    let earnedPoints = 0
    const detailedResults = []

    questions.forEach(question => {
      const userAnswer = userAnswers[question.id]
      const points = question.points || 1
      totalPoints += points

      let isCorrect = false
      if (userAnswer) {
        isCorrect = this.checkAnswer(question, userAnswer.answer)
        if (isCorrect) {
          correctCount++
          earnedPoints += points
        }
      }

      detailedResults.push({
        questionId: question.id,
        userAnswer: userAnswer?.answer || null,
        correctAnswers: question.correct_answers,
        isCorrect,
        points,
        earnedPoints: isCorrect ? points : 0
      })
    })

    const score = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0

    return {
      score,
      correctCount,
      totalQuestions: questions.length,
      earnedPoints,
      totalPoints,
      detailedResults
    }
  }

  // Check if answer is correct
  checkAnswer(question, userAnswer) {
    const correctAnswers = question.correct_answers
    
    switch (question.question_type) {
      case 'multiple-choice':
      case 'true-false':
        return correctAnswers.includes(userAnswer)
      
      case 'multiple-select':
        if (!Array.isArray(userAnswer)) return false
        return userAnswer.length === correctAnswers.length &&
               userAnswer.every(answer => correctAnswers.includes(answer))
      
      case 'fill-in-blank':
        return correctAnswers.some(correct => 
          correct.toLowerCase().trim() === userAnswer.toLowerCase().trim()
        )
      
      case 'matching':
      case 'ordering':
        if (!Array.isArray(userAnswer)) return false
        return JSON.stringify(userAnswer.sort()) === JSON.stringify(correctAnswers.sort())
      
      default:
        return false
    }
  }

  // Get user's quiz attempts
  async getUserQuizAttempts(userId = null, quizId = null) {
    const { data: { user } } = await supabase.auth.getUser()
    const targetUserId = userId || user?.id
    
    if (!targetUserId) {
      throw new Error('User not authenticated')
    }

    let query = supabase
      .from('quiz_attempts')
      .select(`
        *,
        quizzes (
          id,
          title,
          category,
          difficulty,
          passing_score
        )
      `)
      .eq('user_id', targetUserId)
      .order('started_at', { ascending: false })

    if (quizId) {
      query = query.eq('quiz_id', quizId)
    }

    const { data, error } = await query

    if (error) {
      throw error
    }

    return data
  }

  // Get quiz statistics
  async getQuizStatistics(quizId) {
    const { user } = await supabase.auth.getUser()
    if (!user?.user) {
      throw new Error('User not authenticated')
    }

    // Get attempt count
    const { count: attemptCount, error: attemptError } = await supabase
      .from('quiz_attempts')
      .select('*', { count: 'exact', head: true })
      .eq('quiz_id', quizId)

    if (attemptError) {
      throw attemptError
    }

    // Get completion count
    const { count: completionCount, error: completionError } = await supabase
      .from('quiz_attempts')
      .select('*', { count: 'exact', head: true })
      .eq('quiz_id', quizId)
      .not('completed_at', 'is', null)

    if (completionError) {
      throw completionError
    }

    // Get pass count
    const { count: passCount, error: passError } = await supabase
      .from('quiz_attempts')
      .select('*', { count: 'exact', head: true })
      .eq('quiz_id', quizId)
      .eq('passed', true)

    if (passError) {
      throw passError
    }

    // Get average score
    const { data: scores, error: scoresError } = await supabase
      .from('quiz_attempts')
      .select('score')
      .eq('quiz_id', quizId)
      .not('completed_at', 'is', null)

    if (scoresError) {
      throw scoresError
    }

    const averageScore = scores.length > 0 
      ? scores.reduce((sum, s) => sum + s.score, 0) / scores.length
      : 0

    return {
      attemptCount: attemptCount || 0,
      completionCount: completionCount || 0,
      passCount: passCount || 0,
      completionRate: attemptCount > 0 ? (completionCount / attemptCount) * 100 : 0,
      passRate: completionCount > 0 ? (passCount / completionCount) * 100 : 0,
      averageScore: Math.round(averageScore)
    }
  }

  // Utility function to shuffle array
  shuffleArray(array) {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  // Search quizzes
  async searchQuizzes(query, filters = {}) {
    return this.getQuizzes({
      ...filters,
      search: query,
      isPublished: true
    })
  }

  // Get featured quizzes
  async getFeaturedQuizzes(limit = 6) {
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) {
      throw error
    }

    return data
  }
}

// Create singleton instance
export const quizService = new QuizService()

// Utility functions
export const getQuestionTypeLabel = (type) => {
  const labels = {
    'multiple-choice': 'Multiple Choice',
    'true-false': 'True/False',
    'multiple-select': 'Multiple Select',
    'fill-in-blank': 'Fill in the Blank',
    'matching': 'Matching',
    'ordering': 'Ordering'
  }
  return labels[type] || type
}

export const getScoreColor = (score, passingScore = 70) => {
  if (score >= passingScore) return 'green'
  if (score >= passingScore * 0.7) return 'yellow'
  return 'red'
}

export const formatQuizDuration = (timeLimit) => {
  if (!timeLimit) return 'No time limit'
  
  const hours = Math.floor(timeLimit / 3600)
  const minutes = Math.floor((timeLimit % 3600) / 60)
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}